// ============================================================
// TMR Portal Admin — onde os overlays de tela cheia são desenhados
// ============================================================
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';

/** The class of the element every overlay is drawn in — the portal's screen-scoped styles name it too. */
export const OVERLAY_CLASS = 'pa-overlay';

/**
 * Draws a full-screen overlay — a drawer, a dialog and its scrim — at the end of `document.body`, instead of
 * where the screen declares it.
 *
 * **Why it has to leave the screen.** In the site theme every screen is its own stacking context
 * (`isolation: isolate` keeps the page header's glow behind the content, `site.css`), so a
 * `position: fixed` overlay declared inside one can never climb above the top bar: its `z-index` only orders
 * it among the screen's own children, and the frosted top bar was drawn over — and blurred — the top of every
 * drawer. A `Drawer`'s `transform` would also box a dialog opened from inside it into the side panel
 * (`09` §11, N3). From `document.body` the `z-index` is the page's.
 *
 * **Wrapped in {@link OVERLAY_CLASS}**, which the styles that used to reach the overlay through the screen
 * also name — the site theme's headings and the portal's scrollbars.
 *
 * Without a `document` it renders in place: the component tests render to static markup, which has no
 * portals, and what they assert is the overlay's content, not where it is mounted.
 *
 * @param props.children The overlay — its scrim and its panel.
 */
export function OverlayPortal({ children }: { children: ReactNode }) {
  const overlay = <div className={OVERLAY_CLASS}>{children}</div>;
  if (typeof document === 'undefined') return overlay;
  return createPortal(overlay, document.body);
}
